import axios from "axios";
import { useEffect, useState } from "react";
import {
  FiAlertTriangle,
  FiBox,
  FiCalendar,
  FiCheckCircle,
  FiCheckSquare,
  FiClock,
  FiCreditCard,
  FiDollarSign,
  FiPackage,
  FiRotateCw,
  FiSmartphone,
  FiTrash2,
  FiTrendingUp,
  FiUsers,
  FiXCircle,
  FiZap,
} from "react-icons/fi";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import { getStatusTheme } from "../../../components/common/StatusBadge";
import { FiActivity } from "react-icons/fi";
import { confirmAction, showError, showSuccess } from "../../../utils/toastUtils";

const BOOKING_STATUS_ORDER = [
  "pending",
  "confirmed",
  "ongoing",
  "completed",
  "rescheduled",
  "cancelled",
];

const statusIconMap = {
  pending: FiClock,
  ongoing: FiZap,
  confirmed: FiCheckSquare,
  completed: FiCheckCircle,
  cancelled: FiXCircle,
  rescheduled: FiRotateCw,
};

const statusThemeMap = {
  pending: {
    gradient: "from-yellow-50 to-amber-50",
    border: "border-yellow-200",
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-700",
    countColor: "text-yellow-900",
    accentBar: "bg-gradient-to-r from-yellow-400 to-amber-400",
  },
  confirmed: {
    gradient: "from-teal-50 to-emerald-50",
    border: "border-teal-200",
    iconBg: "bg-teal-100",
    iconColor: "text-teal-700",
    countColor: "text-teal-900",
    accentBar: "bg-gradient-to-r from-teal-400 to-emerald-400",
  },
  ongoing: {
    gradient: "from-blue-50 to-indigo-50",
    border: "border-blue-200",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-700",
    countColor: "text-blue-900",
    accentBar: "bg-gradient-to-r from-blue-400 to-indigo-400",
  },
  completed: {
    gradient: "from-green-50 to-lime-50",
    border: "border-green-200",
    iconBg: "bg-green-100",
    iconColor: "text-green-700",
    countColor: "text-green-900",
    accentBar: "bg-gradient-to-r from-green-400 to-lime-400",
  },
  cancelled: {
    gradient: "from-red-50 to-rose-50",
    border: "border-red-200",
    iconBg: "bg-red-100",
    iconColor: "text-red-700",
    countColor: "text-red-900",
    accentBar: "bg-gradient-to-r from-red-400 to-rose-400",
  },
  rescheduled: {
    gradient: "from-purple-50 to-fuchsia-50",
    border: "border-purple-200",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-700",
    countColor: "text-purple-900",
    accentBar: "bg-gradient-to-r from-purple-400 to-fuchsia-400",
  },
};

// ── Delete options config ──────────────────────────────────────────────────
const DELETE_OPTIONS = [
  {
    key: "bookings",
    label: "Service Bookings",
    description: "All service booking records",
    icon: FiCalendar,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    checkBorder: "border-blue-300",
    checkBg: "bg-blue-600",
  },
  {
    key: "packageBookings",
    label: "Package Bookings",
    description: "All package booking records",
    icon: FiPackage,
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
    checkBorder: "border-purple-300",
    checkBg: "bg-purple-600",
  },
  {
    key: "productRequests",
    label: "Product Requests",
    description: "All staff product request records",
    icon: FiBox,
    iconBg: "bg-orange-50",
    iconColor: "text-orange-600",
    checkBorder: "border-orange-300",
    checkBg: "bg-orange-600",
  },
];

// ── Reusable sub-components ────────────────────────────────────────────────
const ServiceMetricCard = ({ label, value, icon: Icon, color, bgColor }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-lg ${bgColor}`}>
        <Icon className={`text-xl ${color}`} />
      </div>
      <p className="text-sm font-medium text-gray-600">{label}</p>
    </div>
    <p className="text-3xl font-bold text-gray-900">{value}</p>
  </div>
);

const StatCard = ({ label, value, icon: Icon, gradient }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 group">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm text-gray-500 mb-2">{label}</p>
        <h2 className="text-3xl font-bold text-gray-900">{value}</h2>
      </div>
      <div
        className={`p-3 rounded-xl ${gradient} group-hover:scale-110 transition-transform duration-300`}
      >
        <Icon className="text-white text-xl" />
      </div>
    </div>
  </div>
);

const StatusCard = ({ status, count }) => {
  const Icon = statusIconMap[status] || FiClock;
  const { label } = getStatusTheme(status);
  const theme = statusThemeMap[status] || statusThemeMap.pending;
  return (
    <div
      className={`rounded-xl border-2 ${theme.border} bg-gradient-to-br ${theme.gradient} p-5 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] relative overflow-hidden group`}
    >
      <div className={`absolute top-0 left-0 right-0 h-1 ${theme.accentBar}`} />
      <div
        className={`absolute -right-6 -top-6 w-24 h-24 ${theme.iconBg} rounded-full opacity-50 group-hover:scale-110 transition-transform duration-300`}
      />
      <div className="flex items-center justify-between mb-4 relative z-1">
        <div
          className={`p-2.5 rounded-xl ${theme.iconBg} shadow-sm group-hover:scale-110 transition-transform duration-300`}
        >
          <Icon className={`text-xl ${theme.iconColor}`} />
        </div>
        <p className={`text-4xl font-bold ${theme.countColor} leading-tight mb-2 relative z-1`}>
          {count}
        </p>
      </div>
      <p className="text-sm text-gray-600 font-medium relative z-1">{label} bookings</p>
      <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${theme.accentBar} opacity-50`} />
    </div>
  );
};

const PaymentCard = ({ label, amount, icon: Icon, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="text-xl text-gray-700" />
      </div>
      <p className="text-sm font-medium text-gray-600">{label}</p>
    </div>
    <p className="text-3xl font-bold text-gray-900">
      ₹{" "}
      {amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </p>
  </div>
);

const LowStockListPanel = ({ lowStock }) => {
  const { threshold, products } = lowStock;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FiBox className="text-lg text-red-600" />
          <h4 className="text-sm font-semibold text-gray-900">Low Stock Products</h4>
        </div>
        <span className="text-xs font-medium text-red-700 bg-red-100 px-3 py-1 rounded-full">
          Below {threshold}
        </span>
      </div>
      <div className="max-h-40 no-scrollbar overflow-y-auto space-y-2">
        {products.map((product) => (
          <div
            key={product._id}
            className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50"
          >
            <div>
              <p className="text-sm font-medium text-gray-900">{product.productName}</p>
              <p className="text-xs text-gray-500">{product.brand}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-gray-400">Qty</p>
              <p className="text-sm font-bold text-red-600">{product.quantity}</p>
            </div>
          </div>
        ))}
      </div>
      {products.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">No low stock products</p>
      )}
    </div>
  );
};

const PendingRequestsPanel = ({ count = 0 }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 h-full flex flex-col justify-center">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <FiClock className="text-lg text-orange-600" />
        <h4 className="text-sm font-semibold text-gray-900">Pending Requests</h4>
      </div>
      <span className="text-xs font-medium text-orange-700 bg-orange-100 px-3 py-1 rounded-full">
        Inventory
      </span>
    </div>
    <div className="text-center">
      <p className="text-5xl font-bold text-orange-600 mb-2">{count}</p>
      <p className="text-sm text-gray-600">Product requests awaiting approval</p>
    </div>
    <div className="mt-6 pt-4 border-t border-gray-100 text-center">
      <p className="text-xs text-gray-500">Review and approve from inventory management</p>
    </div>
  </div>
);

const InventorySummaryCard = ({ totalProducts }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 h-full flex flex-col justify-center">
    <div className="text-center">
      <div className="flex justify-center mb-3">
        <div className="p-3 rounded-xl bg-blue-100">
          <FiPackage className="text-3xl text-blue-600" />
        </div>
      </div>
      <p className="text-sm font-medium text-gray-600 mb-2">Total Products</p>
      <p className="text-5xl font-bold text-gray-900 mb-4">{totalProducts}</p>
      <div className="pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500">Products in inventory</p>
      </div>
    </div>
  </div>
);

// ── Clear Data Panel ───────────────────────────────────────────────────────
const ClearDataPanel = ({ onDeleteSuccess }) => {
  const [selected, setSelected] = useState({
    bookings: false,
    packageBookings: false,
    productRequests: false,
  });
  const [deleting, setDeleting] = useState(false);

  const toggleOption = (key) =>
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const selectedLabels = DELETE_OPTIONS.filter((o) => selected[o.key])
    .map((o) => o.label)
    .join(", ");

  const handleDelete = async () => {
    if (selectedCount === 0) return;

    // SweetAlert2 confirmation via confirmAction (which your project already uses)
    const confirmed = await confirmAction({
      title: "⚠️ Permanently Delete Data?",
      text: `This will delete all records for: ${selectedLabels}. This action CANNOT be undone.`,
      confirmText: "Yes, Delete All",
      cancelText: "Cancel",
      icon: "warning",
    });

    if (!confirmed) return;

    // Second confirmation for safety
    const doubleConfirmed = await confirmAction({
      title: "Are you absolutely sure?",
      text: `Type confirmation: ${selectedCount} collection(s) will be permanently wiped.`,
      confirmText: "Delete Permanently",
      cancelText: "Go Back",
      icon: "error",
    });

    if (!doubleConfirmed) return;

    setDeleting(true);
    try {
      const deleteMap = {
        bookings: `/dashboard/clear/bookings`,
        packageBookings: `/dashboard/clear/package-bookings`,
        productRequests: `/dashboard/clear/product-requests`,
      };

      const promises = Object.entries(selected)
        .filter(([, val]) => val)
        .map(([key]) =>
          axios.delete(`${process.env.REACT_APP_API_URL}${deleteMap[key]}`),
        );

      await Promise.all(promises);

      showSuccess(`Successfully cleared: ${selectedLabels}`);
      setSelected({ bookings: false, packageBookings: false, productRequests: false });
      onDeleteSuccess(); // re-fetch dashboard
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to delete data");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white border-2 border-red-100 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2.5 rounded-xl bg-red-50">
          <FiTrash2 className="text-xl text-red-600" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">Clear Data</h3>
          <p className="text-xs text-gray-500">
            Select collections to permanently delete
          </p>
        </div>
        <div className="ml-auto">
          <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-3 py-1 rounded-full">
            ⚠ Irreversible
          </span>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3 mb-5">
        {DELETE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isChecked = selected[opt.key];
          return (
            <button
              key={opt.key}
              onClick={() => toggleOption(opt.key)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 text-left ${isChecked
                ? "border-red-300 bg-red-50"
                : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-gray-100"
                }`}
            >
              {/* Custom checkbox */}
              <div
                className={`w-5 h-5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${isChecked ? "bg-red-500 border-red-500" : `border-gray-300 bg-white`
                  }`}
              >
                {isChecked && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              {/* Icon */}
              <div className={`p-2 rounded-lg ${opt.iconBg}`}>
                <Icon className={`text-base ${opt.iconColor}`} />
              </div>

              {/* Label */}
              <div className="flex-1">
                <p className={`text-sm font-semibold ${isChecked ? "text-red-700" : "text-gray-800"}`}>
                  {opt.label}
                </p>
                <p className="text-xs text-gray-500">{opt.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Warning */}
      {selectedCount > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
          <FiAlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={15} />
          <p className="text-xs text-amber-700 font-medium">
            {selectedCount} collection{selectedCount > 1 ? "s" : ""} selected: <span className="font-semibold">{selectedLabels}</span>
          </p>
        </div>
      )}

      {/* Delete Button */}
      <button
        onClick={handleDelete}
        disabled={selectedCount === 0 || deleting}
        className={`w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 ${selectedCount === 0
          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
          : "bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-lg hover:shadow-red-200 active:scale-[0.98]"
          }`}
      >
        {deleting ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Deleting…
          </>
        ) : (
          <>
            <FiTrash2 size={15} />
            Delete Selected Data
            {selectedCount > 0 && (
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {selectedCount}
              </span>
            )}
          </>
        )}
      </button>
    </div>
  );
};

// ── Main Dashboard ─────────────────────────────────────────────────────────
const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/dashboard/admin`);
      setData(res.data.data);
    } catch (err) {
      console.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const upiAmount = data?.payments.find((p) => p._id === "UPI_QR")?.amount || 0;
  const cashAmount = data?.payments.find((p) => p._id === "CASH")?.amount || 0;

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF7F3] to-gray-50">
        <div className="px-6 py-2">
          <Breadcrumb title1="Dashboard" title2="Operational Overview" />
        </div>
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 bg-gradient-to-br from-[#FFF7F3] to-gray-50">
      <div className="py-2">
        <Breadcrumb title1="Dashboard" title2="Operational Overview" />
      </div>

      <div className="py-6 space-y-8">
        {/* KPI */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            label="Total Bookings"
            value={data?.bookings?.total}
            icon={FiCalendar}
            gradient="bg-gradient-to-br from-[#f0653a] to-[#d94f2a]"
          />
          <StatCard
            label="Today's Bookings"
            value={data?.bookings?.today}
            icon={FiTrendingUp}
            gradient="bg-gradient-to-br from-blue-500 to-blue-600"
          />
          <StatCard
            label="Completed Today"
            value={data?.bookings?.completedToday}
            icon={FiCheckCircle}
            gradient="bg-gradient-to-br from-green-500 to-green-600"
          />
          <StatCard
            label="Total Staff"
            value={data?.staff?.total}
            icon={FiUsers}
            gradient="bg-gradient-to-br from-purple-500 to-purple-600"
          />
        </div>

        {/* STATUS */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Booking Status Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {BOOKING_STATUS_ORDER.map((status) => {
              const stat = data?.bookings?.byStatus.find((s) => s._id === status);
              return <StatusCard key={status} status={status} count={stat?.count || 0} />;
            })}
          </div>
        </div>

        {/* PAYMENTS */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Payments Snapshot</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <PaymentCard
              label="Total Revenue"
              amount={upiAmount + cashAmount}
              icon={FiDollarSign}
              color="bg-green-100"
            />
            <PaymentCard
              label="UPI Collection"
              amount={upiAmount}
              icon={FiSmartphone}
              color="bg-blue-100"
            />
            <PaymentCard
              label="Cash Collection"
              amount={cashAmount}
              icon={FiCreditCard}
              color="bg-orange-100"
            />
          </div>
        </div>

        {/* SERVICES */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Services Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <ServiceMetricCard
              label="Total Services"
              value={data?.services?.total}
              icon={FiActivity}
              color="text-purple-600"
              bgColor="bg-purple-100"
            />
            <ServiceMetricCard
              label="Active Services"
              value={data?.services?.active}
              icon={FiCheckCircle}
              color="text-green-600"
              bgColor="bg-green-100"
            />
            <ServiceMetricCard
              label="Inactive Services"
              value={data?.services?.inactive}
              icon={FiXCircle}
              color="text-gray-600"
              bgColor="bg-gray-100"
            />
          </div>
        </div>

        {/* ALERTS */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Inventory & Alerts</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <LowStockListPanel lowStock={data?.inventory?.lowStock} />
            <PendingRequestsPanel count={data?.inventory?.pendingRequestedProducts} />
            <InventorySummaryCard totalProducts={data?.inventory?.totalProducts} />
          </div>
        </div>

        {/* DANGER ZONE — Clear Data */}
        <div>
          <h3 className="text-lg font-semibold mb-3 text-red-700 flex items-center gap-2">
            <FiAlertTriangle className="text-red-500" />
            Danger Zone
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ClearDataPanel onDeleteSuccess={fetchDashboard} />

            {/* Info card alongside */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-2 rounded-xl bg-yellow-50">
                    <FiAlertTriangle className="text-yellow-600 text-lg" />
                  </div>
                  <h4 className="font-bold text-gray-800">Before You Delete</h4>
                </div>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    Deleted records <strong>cannot be recovered</strong>. There is no undo.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    Deleting <strong>Service Bookings</strong> will remove all booking history, payment records, and OTPs.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    Deleting <strong>Package Bookings</strong> will remove all package orders and their associated data.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    Deleting <strong>Product Requests</strong> will remove all staff inventory requests.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 flex-shrink-0" />
                    You will be asked to confirm twice before any deletion occurs.
                  </li>
                </ul>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  This action is intended for data cleanup / testing environments only. Use with extreme caution in production.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Skeleton ───────────────────────────────────────────────────────────────
const SkeletonBlock = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-200 ${className}`} />
);

const DashboardSkeleton = () => (
  <div className="p-6 space-y-8">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      {[...Array(4)].map((_, i) => <SkeletonBlock key={i} className="h-28" />)}
    </div>
    <div>
      <SkeletonBlock className="h-6 w-64 mb-4" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => <SkeletonBlock key={i} className="h-32" />)}
      </div>
    </div>
    <div>
      <SkeletonBlock className="h-6 w-48 mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[...Array(3)].map((_, i) => <SkeletonBlock key={i} className="h-28" />)}
      </div>
    </div>
    <div>
      <SkeletonBlock className="h-6 w-56 mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[...Array(3)].map((_, i) => <SkeletonBlock key={i} className="h-28" />)}
      </div>
    </div>
    <div>
      <SkeletonBlock className="h-6 w-56 mb-4" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {[...Array(3)].map((_, i) => <SkeletonBlock key={i} className="h-72" />)}
      </div>
    </div>
  </div>
);

export default Dashboard;










// import axios from "axios";
// import { useEffect, useState } from "react";
// import {
//   FiBox,
//   FiCalendar,
//   FiCheckCircle,
//   FiCheckSquare,
//   FiClock,
//   FiCreditCard,
//   FiDollarSign,
//   FiPackage,
//   FiRefreshCw,
//   FiRotateCw,
//   FiSmartphone,
//   FiTrendingUp,
//   FiUsers,
//   FiXCircle,
//   FiZap,
// } from "react-icons/fi";
// import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
// import { getStatusTheme } from "../../../components/common/StatusBadge";
// import { FiActivity } from "react-icons/fi";
// import { showSuccess } from "../../../utils/toastUtils";

// const BOOKING_STATUS_ORDER = [
//   "pending",
//   "confirmed",
//   "ongoing",
//   "completed",
//   "rescheduled",
//   "cancelled",
// ];

// const statusIconMap = {
//   pending: FiClock,
//   ongoing: FiZap,
//   confirmed: FiCheckSquare,
//   completed: FiCheckCircle,
//   cancelled: FiXCircle,
//   rescheduled: FiRotateCw,
// };

// const statusThemeMap = {
//   pending: {
//     gradient: "from-yellow-50 to-amber-50",
//     border: "border-yellow-200",
//     iconBg: "bg-yellow-100",
//     iconColor: "text-yellow-700",
//     countColor: "text-yellow-900",
//     accentBar: "bg-gradient-to-r from-yellow-400 to-amber-400",
//   },

//   confirmed: {
//     gradient: "from-teal-50 to-emerald-50",
//     border: "border-teal-200",
//     iconBg: "bg-teal-100",
//     iconColor: "text-teal-700",
//     countColor: "text-teal-900",
//     accentBar: "bg-gradient-to-r from-teal-400 to-emerald-400",
//   },

//   ongoing: {
//     gradient: "from-blue-50 to-indigo-50",
//     border: "border-blue-200",
//     iconBg: "bg-blue-100",
//     iconColor: "text-blue-700",
//     countColor: "text-blue-900",
//     accentBar: "bg-gradient-to-r from-blue-400 to-indigo-400",
//   },

//   completed: {
//     gradient: "from-green-50 to-lime-50",
//     border: "border-green-200",
//     iconBg: "bg-green-100",
//     iconColor: "text-green-700",
//     countColor: "text-green-900",
//     accentBar: "bg-gradient-to-r from-green-400 to-lime-400",
//   },

//   cancelled: {
//     gradient: "from-red-50 to-rose-50",
//     border: "border-red-200",
//     iconBg: "bg-red-100",
//     iconColor: "text-red-700",
//     countColor: "text-red-900",
//     accentBar: "bg-gradient-to-r from-red-400 to-rose-400",
//   },

//   rescheduled: {
//     gradient: "from-purple-50 to-fuchsia-50",
//     border: "border-purple-200",
//     iconBg: "bg-purple-100",
//     iconColor: "text-purple-700",
//     countColor: "text-purple-900",
//     accentBar: "bg-gradient-to-r from-purple-400 to-fuchsia-400",
//   },
// };

// const ServiceMetricCard = ({ label, value, icon: Icon, color, bgColor }) => (
//   <div className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
//     <div className="flex items-center gap-3 mb-3">
//       <div className={`p-2 rounded-lg ${bgColor}`}>
//         <Icon className={`text-xl ${color}`} />
//       </div>
//       <p className="text-sm font-medium text-gray-600">{label}</p>
//     </div>
//     <p className="text-3xl font-bold text-gray-900">{value}</p>
//   </div>
// );

// const StatCard = ({ label, value, icon: Icon, gradient }) => (
//   <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 group">
//     <div className="flex items-start justify-between">
//       <div className="flex-1">
//         <p className="text-sm text-gray-500 mb-2">{label}</p>
//         <h2 className="text-3xl font-bold text-gray-900">{value}</h2>
//       </div>
//       <div
//         className={`p-3 rounded-xl ${gradient} group-hover:scale-110 transition-transform duration-300`}
//       >
//         <Icon className="text-white text-xl" />
//       </div>
//     </div>
//   </div>
// );

// const StatusCard = ({ status, count }) => {
//   const Icon = statusIconMap[status] || FiClock;
//   const { label } = getStatusTheme(status);
//   const theme = statusThemeMap[status] || statusThemeMap.pending;

//   return (
//     <div
//       className={`rounded-xl border-2 ${theme.border} bg-gradient-to-br ${theme.gradient} p-5 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] relative overflow-hidden group`}
//     >
//       <div
//         className={`absolute top-0 left-0 right-0 h-1 ${theme.accentBar}`}
//       ></div>

//       {/* Decorative circle in background */}
//       <div
//         className={`absolute -right-6 -top-6 w-24 h-24 ${theme.iconBg} rounded-full opacity-50 group-hover:scale-110 transition-transform duration-300`}
//       ></div>

//       <div className="flex items-center justify-between mb-4 relative z-1">
//         <div
//           className={`p-2.5 rounded-xl ${theme.iconBg} shadow-sm group-hover:scale-110 transition-transform duration-300`}
//         >
//           <Icon className={`text-xl ${theme.iconColor}`} />
//         </div>
//         <p
//           className={`text-4xl font-bold ${theme.countColor} leading-tight mb-2 relative z-1`}
//         >
//           {count}
//         </p>{" "}
//       </div>

//       {/* Helper text */}
//       <p className="text-sm text-gray-600 font-medium relative z-1">
//         {label} bookings
//       </p>

//       {/* Bottom accent line */}
//       <div
//         className={`absolute bottom-0 left-0 right-0 h-0.5 ${theme.accentBar} opacity-50`}
//       ></div>
//     </div>
//   );
// };

// const PaymentCard = ({ label, amount, icon: Icon, color }) => (
//   <div className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
//     <div className="flex items-center gap-3 mb-3">
//       <div className={`p-2 rounded-lg ${color}`}>
//         <Icon className="text-xl text-gray-700" />
//       </div>
//       <p className="text-sm font-medium text-gray-600">{label}</p>
//     </div>
//     <p className="text-3xl font-bold text-gray-900">
//       ₹{" "}
//       {amount.toLocaleString("en-IN", {
//         minimumFractionDigits: 2,
//         maximumFractionDigits: 2,
//       })}
//     </p>
//   </div>
// );

// const LowStockListPanel = ({ lowStock }) => {
//   const { threshold, products } = lowStock;

//   return (
//     <div className="bg-white border border-gray-200 rounded-xl p-5 h-full">
//       {/* Header */}
//       <div className="flex items-center justify-between mb-4">
//         <div className="flex items-center gap-2">
//           <FiBox className="text-lg text-red-600" />
//           <h4 className="text-sm font-semibold text-gray-900">
//             Low Stock Products
//           </h4>
//         </div>

//         <span className="text-xs font-medium text-red-700 bg-red-100 px-3 py-1 rounded-full">
//           Below {threshold}
//         </span>
//       </div>

//       {/* List */}
//       <div className="max-h-40 no-scrollbar overflow-y-auto  space-y-2">
//         {products.map((product) => (
//           <div
//             key={product._id}
//             className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50"
//           >
//             <div>
//               <p className="text-sm font-medium text-gray-900">
//                 {product.productName}
//               </p>
//               <p className="text-xs text-gray-500">{product.brand}</p>
//             </div>

//             <div className="text-right">
//               <p className="text-[11px] text-gray-400">Qty</p>
//               <p className="text-sm font-bold text-red-600">
//                 {product.quantity}
//               </p>
//             </div>
//           </div>
//         ))}
//       </div>

//       {products.length === 0 && (
//         <p className="text-sm text-gray-500 text-center py-4">
//           No low stock products
//         </p>
//       )}
//     </div>
//   );
// };

// const PendingRequestsPanel = ({ count = 0 }) => {
//   return (
//     <div className="bg-white border border-gray-200 rounded-xl p-5 h-full flex flex-col justify-center">
//       {/* Header */}
//       <div className="flex items-center justify-between mb-4">
//         <div className="flex items-center gap-2">
//           <FiClock className="text-lg text-orange-600" />
//           <h4 className="text-sm font-semibold text-gray-900">
//             Pending Requests
//           </h4>
//         </div>

//         <span className="text-xs font-medium text-orange-700 bg-orange-100 px-3 py-1 rounded-full">
//           Inventory
//         </span>
//       </div>

//       {/* Count */}
//       <div className="text-center">
//         <p className="text-5xl font-bold text-orange-600 mb-2">{count}</p>
//         <p className="text-sm text-gray-600">
//           Product requests awaiting approval
//         </p>
//       </div>

//       {/* Footer hint */}
//       <div className="mt-6 pt-4 border-t border-gray-100 text-center">
//         <p className="text-xs text-gray-500">
//           Review and approve from inventory management
//         </p>
//       </div>
//     </div>
//   );
// };

// const InventorySummaryCard = ({ totalProducts }) => {
//   return (
//     <div className="bg-white border border-gray-200 rounded-xl p-5 h-full flex flex-col justify-center">
//       <div className="text-center">
//         <div className="flex justify-center mb-3">
//           <div className="p-3 rounded-xl bg-blue-100">
//             <FiPackage className="text-3xl text-blue-600" />
//           </div>
//         </div>

//         <p className="text-sm font-medium text-gray-600 mb-2">Total Products</p>
//         <p className="text-5xl font-bold text-gray-900 mb-4">{totalProducts}</p>

//         <div className="pt-4 border-t border-gray-100">
//           <p className="text-xs text-gray-500">Products in inventory</p>
//         </div>
//       </div>
//     </div>
//   );
// };

// const Dashboard = () => {
//   const [data, setData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [refreshing, setRefreshing] = useState(false);

//   const fetchDashboard = async (isRefresh = false) => {
//     try {
//       isRefresh ? setRefreshing(true) : setLoading(true);
//       const res = await axios.get(
//         `${process.env.REACT_APP_API_URL}/dashboard/admin`,
//       );
//       setData(res.data.data);
//       if (isRefresh) {
//         showSuccess("Refreshed");
//       }
//     } catch (err) {
//       console.error("Failed to load dashboard data");
//     } finally {
//       setLoading(false);
//       setRefreshing(false);
//     }
//   };

//   useEffect(() => {
//     fetchDashboard();
//   }, []);

//   const refreshBtn = () => (
//     <button
//       onClick={() => fetchDashboard(true)}
//       disabled={refreshing}
//       className="flex items-center gap-2 px-5 py-2.5 text-sm rounded-xl bg-gradient-to-r from-[#f0653a] to-[#d94f2a] text-white font-semibold hover:shadow-lg transition-all"
//     >
//       <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
//       Refresh
//     </button>
//   );

//   const upiAmount = data?.payments.find((p) => p._id === "UPI_QR")?.amount || 0;
//   const cashAmount = data?.payments.find((p) => p._id === "CASH")?.amount || 0;

//   if (loading && !data) {
//     return (
//       <div className="min-h-screen  bg-gradient-to-br from-[#FFF7F3] to-gray-50">
//         {/* Header stays visible */}
//         <div className="px-6 py-2">
//           <Breadcrumb
//             title1="Dashboard"
//             title2="Operational Overview"
//             customButton={refreshBtn}
//           />
//         </div>

//         {/* Skeleton content */}
//         <DashboardSkeleton />
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen px-6 bg-gradient-to-br from-[#FFF7F3] to-gray-50">
//       <div className="py-2">
//         <Breadcrumb
//           title1="Dashboard"
//           title2="Operational Overview"
//           customButton={refreshBtn}
//         />
//       </div>

//       <div className="py-6 space-y-8">
//         {/* KPI */}
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
//           <StatCard
//             label="Total Bookings"
//             value={data?.bookings?.total}
//             icon={FiCalendar}
//             gradient="bg-gradient-to-br from-[#f0653a] to-[#d94f2a]"
//           />
//           <StatCard
//             label="Today's Bookings"
//             value={data?.bookings?.today}
//             icon={FiTrendingUp}
//             gradient="bg-gradient-to-br from-blue-500 to-blue-600"
//           />
//           <StatCard
//             label="Completed Today"
//             value={data?.bookings?.completedToday}
//             icon={FiCheckCircle}
//             gradient="bg-gradient-to-br from-green-500 to-green-600"
//           />
//           <StatCard
//             label="Total Staff"
//             value={data?.staff?.total}
//             icon={FiUsers}
//             gradient="bg-gradient-to-br from-purple-500 to-purple-600"
//           />
//         </div>
//         {/* STATUS */}
//         <div>
//           <h3 className="text-lg font-semibold mb-3">
//             Booking Status Overview
//           </h3>
//           <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
//             {BOOKING_STATUS_ORDER.map((status) => {
//               const stat = data?.bookings?.byStatus.find(
//                 (s) => s._id === status,
//               );

//               return (
//                 <StatusCard
//                   key={status}
//                   status={status}
//                   count={stat?.count || 0}
//                 />
//               );
//             })}
//           </div>
//         </div>
//         {/* PAYMENTS */}
//         <div>
//           <h3 className="text-lg font-semibold mb-3">Payments Snapshot</h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
//             <PaymentCard
//               label="Total Revenue"
//               amount={upiAmount + cashAmount}
//               icon={FiDollarSign}
//               color="bg-green-100"
//             />
//             <PaymentCard
//               label="UPI Collection"
//               amount={upiAmount}
//               icon={FiSmartphone}
//               color="bg-blue-100"
//             />
//             <PaymentCard
//               label="Cash Collection"
//               amount={cashAmount}
//               icon={FiCreditCard}
//               color="bg-orange-100"
//             />
//           </div>
//         </div>
//         <div>
//           <h3 className="text-lg font-semibold mb-3">Services Overview</h3>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
//             <ServiceMetricCard
//               label="Total Services"
//               value={data?.services?.total}
//               icon={FiActivity}
//               color="text-purple-600"
//               bgColor="bg-purple-100"
//             />
//             <ServiceMetricCard
//               label="Active Services"
//               value={data?.services?.active}
//               icon={FiCheckCircle}
//               color="text-green-600"
//               bgColor="bg-green-100"
//             />
//             <ServiceMetricCard
//               label="Inactive Services"
//               value={data?.services?.inactive}
//               icon={FiXCircle}
//               color="text-gray-600"
//               bgColor="bg-gray-100"
//             />
//           </div>
//         </div>
//         {/* ALERTS */}
//         <div>
//           <h3 className="text-lg font-semibold mb-3">Inventory & Alerts</h3>
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
//             {/* Low Stock Products */}
//             <LowStockListPanel lowStock={data?.inventory?.lowStock} />

//             {/* Pending Product Requests */}
//             <PendingRequestsPanel
//               requests={data?.inventory?.pendingRequestedProducts}
//             />

//             {/* Total Products Summary */}
//             <InventorySummaryCard
//               totalProducts={data?.inventory?.totalProducts}
//             />
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const SkeletonBlock = ({ className = "" }) => (
//   <div className={`animate-pulse rounded-xl bg-gray-200 ${className}`} />
// );

// const DashboardSkeleton = () => {
//   return (
//     <div className="p-6 space-y-8">
//       {/* KPI Skeleton */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
//         {[...Array(4)].map((_, i) => (
//           <SkeletonBlock key={i} className="h-28" />
//         ))}
//       </div>

//       {/* Status Cards Skeleton */}
//       <div>
//         <SkeletonBlock className="h-6 w-64 mb-4" />
//         <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
//           {[...Array(6)].map((_, i) => (
//             <SkeletonBlock key={i} className="h-32" />
//           ))}
//         </div>
//       </div>

//       {/* Payments Skeleton */}
//       <div>
//         <SkeletonBlock className="h-6 w-48 mb-4" />
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
//           {[...Array(3)].map((_, i) => (
//             <SkeletonBlock key={i} className="h-28" />
//           ))}
//         </div>
//       </div>

//       {/* Services Skeleton */}
//       <div>
//         <SkeletonBlock className="h-6 w-56 mb-4" />
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
//           {[...Array(3)].map((_, i) => (
//             <SkeletonBlock key={i} className="h-28" />
//           ))}
//         </div>
//       </div>

//       {/* Inventory & Alerts Skeleton */}
//       <div>
//         <SkeletonBlock className="h-6 w-56 mb-4" />
//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
//           {[...Array(3)].map((_, i) => (
//             <SkeletonBlock key={i} className="h-72" />
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Dashboard;
